import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useStaticQuery, graphql } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';

// import Heading from 'components/shared/heading';
import Input from 'components/shared/input';
import Checkbox from 'components/shared/checkbox';
import Button from 'components/shared/button';

import styles from './styles.module.scss';

// Validation schema for Scope Inputs
const validationSchema = yup.object().shape({
  intercoms: yup
    .number()
    .min(0, 'Must be 0 or greater')
    .transform((value) => (Number.isNaN(value) ? 0 : value)),
  cardReaders: yup
    .number()
    .min(0, 'Must be 0 or greater')
    .transform((value) => (Number.isNaN(value) ? 0 : value)),
  vehicleReaders: yup
    .number()
    .min(0, 'Must be 0 or greater')
    .transform((value) => (Number.isNaN(value) ? 0 : value)),
  smartLocks: yup
    .number()
    .min(0, 'Must be 0 or greater')
    .transform((value) => (Number.isNaN(value) ? 0 : value)),
  elevatorAccess: yup.boolean(),
  installationNeeded: yup.boolean(),
  constructionType: yup.string().required('Construction type is required'),
  camerasNeeded: yup.boolean(),
});

const ScopeInputs = ({ initialData, onComplete }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: initialData,
    mode: 'onChange',
  });

  // Query for product images
  const { intercomImage, cardReaderImage, vehicleReaderImage } = useStaticQuery(graphql`
    query {
      intercomImage: file(relativePath: { eq: "estimate-generator/intercom.jpg" }) {
        childImageSharp {
          gatsbyImageData(width: 64, placeholder: BLURRED, formats: [AUTO, WEBP])
        }
      }
      cardReaderImage: file(relativePath: { eq: "estimate-generator/card-reader.jpg" }) {
        childImageSharp {
          gatsbyImageData(width: 64, placeholder: BLURRED, formats: [AUTO, WEBP])
        }
      }
      vehicleReaderImage: file(relativePath: { eq: "estimate-generator/vehicle-reader.jpg" }) {
        childImageSharp {
          gatsbyImageData(width: 64, placeholder: BLURRED, formats: [AUTO, WEBP])
        }
      }
    }
  `);

  const intercoms = watch('intercoms');
  const cardReaders = watch('cardReaders');
  const vehicleReaders = watch('vehicleReaders');

  // If any of the quantities are greater than 0, then the user can go to the next step
  const canGoNext = useMemo(
    () =>
      (intercoms && intercoms > 0) ||
      (cardReaders && cardReaders > 0) ||
      (vehicleReaders && vehicleReaders > 0),
    [intercoms, cardReaders, vehicleReaders]
  );

  const onSubmit = useCallback(
    (data) => {
      onComplete(data);
    },
    [onComplete]
  );

  return (
    <div className={styles.scopeInputs} id="estimate-scope-inputs">
      {/* <Heading className={styles.stepTitle} tag="h3" size="md">
        Scope & Requirements
      </Heading> */}

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.productsGrid}>
          <div className={styles.productCard}>
            <div className={styles.productHeader}>
              <div className={styles.productIcon}>
                <GatsbyImage image={getImage(intercomImage)} alt="Intercom" />
              </div>
              <div className={styles.productInfo}>
                <h4>Intercoms</h4>
                <p>Video intercom systems</p>
              </div>
            </div>
            <div className={styles.quantityInput}>
              <label htmlFor="intercoms">Quantity:</label>
              <Input
                {...register('intercoms')}
                id="intercoms"
                type="number"
                min="0"
                placeholder="0"
                withBorder
              />
            </div>
          </div>

          <div className={styles.productCard}>
            <div className={styles.productHeader}>
              <div className={styles.productIcon}>
                <GatsbyImage image={getImage(cardReaderImage)} alt="Card Reader" />
              </div>
              <div className={styles.productInfo}>
                <h4>Card Readers</h4>
                <p>Access control readers</p>
              </div>
            </div>
            <div className={styles.quantityInput}>
              <label htmlFor="cardReaders">Quantity:</label>
              <Input
                {...register('cardReaders')}
                id="cardReaders"
                type="number"
                min="0"
                placeholder="0"
                withBorder
              />
            </div>
          </div>

          <div className={styles.productCard}>
            <div className={styles.productHeader}>
              <div className={styles.productIcon}>
                <GatsbyImage image={getImage(vehicleReaderImage)} alt="Vehicle Reader" />
              </div>
              <div className={styles.productInfo}>
                <h4>Vehicle Readers</h4>
                <p>Parking access control</p>
              </div>
            </div>
            <div className={styles.quantityInput}>
              <label htmlFor="vehicleReaders">Quantity:</label>
              <Input
                {...register('vehicleReaders')}
                id="vehicleReaders"
                type="number"
                min="0"
                placeholder="0"
                withBorder
              />
            </div>
          </div>

          {/* <div className={styles.productCard}>
            <div className={styles.productHeader}>
              <div className={styles.productIcon}>
                <GatsbyImage image={getImage(smartLockImage)} alt="Smart Lock" />
              </div>
              <div className={styles.productInfo}>
                <h4>Smart Locks</h4>
                <p>Electronic door locks</p>
              </div>
            </div>
            <div className={styles.quantityInput}>
              <label htmlFor="smartLocks">Quantity:</label>
              <Input
                ref={register}
                name="smartLocks"
                id="smartLocks"
                type="number"
                min="0"
                placeholder="0"
                withBorder
              />
            </div>
          </div> */}
        </div>

        <div className={styles.optionsSection}>
          <div className={styles.checkboxGroup}>
            <Checkbox
              {...register('elevatorAccess')}
              id="elevatorAccess"
              label="Elevator Access Control"
              error={errors.elevatorAccess}
            />
            <Checkbox
              {...register('installationNeeded')}
              id="installationNeeded"
              label="Installation Help Needed"
              error={errors.installationNeeded}
            />
            <Checkbox
              {...register('camerasNeeded')}
              id="camerasNeeded"
              label="Want Security Cameras?"
              error={errors.camerasNeeded}
            />
          </div>

          <div className={styles.radioGroup}>
            <div className={styles.label}>Construction Type</div>
            <div className={styles.radioOptions}>
              <Checkbox
                {...register('constructionType')}
                type="radio"
                id="constructionTypeExisting"
                label="Existing System"
                value="existing"
                defaultChecked
                error={errors.constructionType}
              />
              <Checkbox
                {...register('constructionType')}
                type="radio"
                id="constructionTypeNew"
                label="New Construction"
                value="new"
                error={errors.constructionType}
              />
            </div>
          </div>
        </div>

        <div className={styles.navigation}>
          <Button theme="accent-primary" type="submit" disabled={!canGoNext}>
            Next
          </Button>
        </div>
      </form>
    </div>
  );
};

ScopeInputs.propTypes = {
  initialData: PropTypes.object.isRequired,
  onComplete: PropTypes.func.isRequired,
};

export default ScopeInputs;
